import { lazy, Suspense } from "react";
import { Route, Routes } from "react-router-dom";

import LookupTester from "@/pages/LookupTester";

const BrandInvitationPage = lazy(
  () => import("@/pages/manager/utils/members/BrandInvitationPage"),
);
const Dashboard = lazy(() => import("@/pages/client/Dashboard"));
const BillingPage = lazy(() => import("@/pages/billing/Billing"));

//Routes
import PropertyRoutes from "./PropertyRoutes";
const OrganizationRoutes = lazy(() => import("./OrganizationRoutes"));
const AccommodationRoutes = lazy(() => import("./AccommodationRoutes"));

// layouts
import ClientLayout from "@/layouts/ClientLayout";
const MainLayout = lazy(() => import("@/layouts/MainLayout"));
const AuthLayout = lazy(() => import("@/layouts/AuthLayout"));

// pages – public / marketing
const Home = lazy(() => import("@/pages/landing/Home"));
const Properties = lazy(() => import("@/pages/properties/Properties"));
const About = lazy(() => import("@/pages/landing/about/About"));
const Contact = lazy(() => import("@/pages/landing/contact/Contact"));
const Pricing = lazy(() => import("@/pages/landing/pricing/Pricing"));
const Hostel = lazy(() => import("@/pages/manager/workspace/units/Hostel"));

// auth
const Login = lazy(() => import("@/pages/auth/Login"));
const Register = lazy(() => import("@/pages/auth/Register"));
const ForgotPassword = lazy(() => import("@/pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/auth/ResetPassword"));

// components & utils
import PageNotFound from "@/components/utils/PageNotFound";
import GenericLoader from "@/components/ui/GenericLoader";
const ProtectedRoute = lazy(() => import("@/pages/auth/ProtectedRoute"));

const LandlordVerification = lazy(
  () =>
    import("@/pages/manager/utils/landlordVerification/LandlordVerification"),
);

const PrivacyPolicy = lazy(
  () => import("@/components/utils/PrivatePolicy.tsx"),
);
const TermsAndConditions = lazy(
  () => import("@/components/utils/Terms&Conditions"),
);

export default function AppRoutes() {
  return (
    <Suspense fallback={<GenericLoader type={"page"} message={"Getting things ready"} />}>
      <Routes>
        <Route path="/lookups" element={<LookupTester />} />
        <Route path="gl" element={<GenericLoader type={'page'} />} />
        {/* ────────────────────────────────────────
            AUTH / no layout (login, register, reset)
        ──────────────────────────────────────── */}
        <Route element={<AuthLayout />}>
          <Route path="/auth/login" element={<Login />} />
          <Route path="/auth/register" element={<Register />} />
          <Route path="/auth/forgot-password" element={<ForgotPassword />} />
          <Route path="/auth/reset-password" element={<ResetPassword />} />
          {/* landlord verification */}
          <Route
            path="/landlord/verification"
            element={<LandlordVerification />}
          />
        </Route>

        {/* organization panel */}
        <Route path="/org/*" element={<OrganizationRoutes />} />

        <Route path="/brand-invitation" element={<BrandInvitationPage />} />

        {/* ────────────────────────────────────────
            LANDLORD PROTECTED AREA
        ──────────────────────────────────────── */}
        <Route element={<ProtectedRoute />}>
        <Route path="/billing/*" element={<BillingPage />} />
        </Route>

        {/* ────────────────────────────────────────
            MAIN SITE (with header/footer)
        ──────────────────────────────────────── */}
        <Route element={<MainLayout />}>
          {/* Public landing pages */}
          <Route index element={<Properties />} />
          <Route path="/main" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route
            path="/terms-and-conditions"
            element={<TermsAndConditions />}
          />

          <Route path="/accommodations/*" element={<AccommodationRoutes />} />
          <Route path="/properties/*" element={<PropertyRoutes />} />

          <Route
            element={
              <ProtectedRoute
              // options={{ roles: ["client", "landlord"] }}
              />
            }
          >
            <Route path="/hostel/:id" element={<Hostel />} />
          </Route>
        </Route>
        {/* ========================================================================== */}
        {/* Client protected area */}
        <Route
          element={
            <ProtectedRoute>
              <ClientLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/client/*" element={<Dashboard />} />
        </Route>

        {/* 404 – catch-all */}
        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </Suspense>
  );
}
